// Versão: 4.06
// Data..: 2026-10-16
// Inbox de WhatsApp: abas Conversas | Grupos. Recebe/responde/inicia (contato e grupo),
// mostra foto do contato (com fallback de iniciais) e permite vincular/criar cliente.
// v4.06: horário de cada mensagem na bolha agora mostra DATA + hora (dd/mm/aaaa hh:mm).
// v4.05: bolha OUTBOUND exibe o nome do atendente (authorName) em rótulo discreto
// acima do corpo, só quando há autor; INBOUND não mostra. Layout preservado.
// v4.03: aba Grupos exibe erro visível quando a Evolution falha (loadGroups lê
// r.error e guarda em groupsError; .catch define mensagem genérica). Sem erro e
// lista vazia mantém "Nenhum grupo encontrado na instância."
// v4.04: thread rola internamente (scrollTop do container, não a página); imagem abre
// em popup (ImageThumb); composer grava e envia áudio (MediaRecorder, OGG/Opus).
// v4.02: botão de ligar (click-to-call 3CX) no cabeçalho da conversa individual.
// v4.01: composer envia mídia (imagem/arquivo/áudio/vídeo); mídia é exibida na thread;
// nova conversa busca contatos já cadastrados (autocomplete por nome/telefone).

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  TicketStatus,
  Channel,
  MessageDirection,
  UserRole,
  type Ticket,
  type Message,
  type Company,
  type WhatsAppGroup,
  type Attachment,
  type User,
  type Paginated,
} from '@makweb/shared';
import { api } from '../lib/api.js';
import { uploadFile } from '../lib/upload.js';
import { config } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import { useDialog } from '../context/DialogContext.js';
import { PageHeader } from '../components/PageHeader.js';
import { CallButton } from '../components/CallButton.js';
import { Modal } from '../components/Modal.js';
import { ImageThumb } from '../components/ImageThumb.js';

// Monta a URL pública (redireciona para o S3) de um anexo a partir da s3Key.
// Mesmo padrão do RichEditor: base64url da chave num único segmento de rota.
function attachmentUrl(s3Key: string): string {
  const encoded = btoa(s3Key).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `${config.apiUrl}/public/attachments/${encoded}`;
}

// Renderiza um anexo conforme o tipo (imagem/vídeo/áudio inline; demais como link).
function AttachmentView({ att, outbound }: { att: Attachment; outbound: boolean }) {
  const url = attachmentUrl(att.s3Key);
  const type = (att.contentType ?? '').toLowerCase();
  if (type.startsWith('image/')) {
    return <ImageThumb src={url} alt={att.fileName} thumbStyle={{ maxWidth: 240 }} />;
  }
  if (type.startsWith('video/')) {
    return <video src={url} controls style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />;
  }
  if (type.startsWith('audio/')) {
    return <audio src={url} controls style={{ width: 220, maxWidth: '100%' }} />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ color: outbound ? '#fff' : 'var(--brand)', textDecoration: 'underline', wordBreak: 'break-word' }}>
      📎 {att.fileName}
    </a>
  );
}

interface TicketDetail { ticket: Ticket; messages: Message[]; }

// Cor estável derivada do texto (para o avatar de cada contato).
const AVATAR_COLORS = ['#0088cc', '#6c3ce0', '#10b981', '#f97316', '#ef4444', '#0ea5e9', '#8b5cf6'];
function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}
function timeShort(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}
// Foto do contato guardada em sourceMetadata.avatarUrl (quando a Evolution retornou).
function avatarUrlOf(t?: Ticket | null): string | undefined {
  const meta = t?.sourceMetadata as { avatarUrl?: string } | undefined;
  return meta?.avatarUrl;
}
function isGroupTicket(t?: Ticket | null): boolean {
  const meta = t?.sourceMetadata as { isGroup?: boolean } | undefined;
  return !!meta?.isGroup || !!t?.requesterContact?.includes('@g.us');
}

// Avatar reutilizável: usa foto quando houver; senão, iniciais coloridas.
function Avatar({ name, url, group }: { name: string; url?: string; group?: boolean }) {
  if (url) {
    return <span className="wa-avatar" style={{ padding: 0, overflow: 'hidden' }}><img src={url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></span>;
  }
  return (
    <span className="wa-avatar" style={{ background: group ? '#10b981' : avatarColor(name) }}>
      {group ? '👥' : initials(name)}
    </span>
  );
}

type Tab = 'conversas' | 'grupos';

export function WhatsappPage() {
  const toast = useToast();
  const { confirm } = useDialog();
  const [tab, setTab] = useState<Tab>('conversas');
  const [convos, setConvos] = useState<Ticket[]>([]);
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [search, setSearch] = useState('');
  const [starting, setStarting] = useState(false);
  const [linking, setLinking] = useState(false);
  // Anexos pendentes (mídia a enviar) e estado de upload.
  const [pending, setPending] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  // Gravação de áudio (mensagem de voz) do atendente.
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Conversas ativas do canal whatsapp (varre status ativos), consolidadas por contato.
  const loadConvos = () => {
    Promise.all(
      [TicketStatus.OPEN, TicketStatus.IN_PROGRESS, TicketStatus.WAITING_CLIENT].map((s) =>
        api.get<Paginated<Ticket>>(`/tickets?status=${s}&limit=100`).catch(() => ({ items: [] as Ticket[] })),
      ),
    ).then((res) => {
      const all = res.flatMap((r) => r.items).filter((t) => t.channel === Channel.WHATSAPP);
      all.sort((a, b) => ((b.lastMessageAt ?? b.updatedAt) > (a.lastMessageAt ?? a.updatedAt) ? 1 : -1));
      const seen = new Set<string>();
      const deduped = all.filter((t) => {
        const key = (t.requesterContact ?? t.id).replace(/[^0-9a-z@.]/gi, '') || t.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setConvos(deduped);
      setSelected((cur) => cur ?? deduped[0]?.id ?? null);
    });
  };

  // Grupos da instância (sempre visíveis na aba Grupos), via Evolution. Quando a
  // Evolution falha, o backend responde 200 com { items: [], error }; guardamos o
  // erro em groupsError para a aba exibir a causa em vez de "nenhum grupo".
  const loadGroups = () => {
    setGroupsError(null);
    api.get<{ items: WhatsAppGroup[]; error?: string }>('/whatsapp/groups')
      .then((r) => {
        setGroups(r.items ?? []);
        if (r.error) setGroupsError(`Falha ao carregar grupos: ${r.error}`);
      })
      .catch(() => {
        // Erro de rede/HTTP: api.get só expõe a mensagem, não o status; usamos texto genérico.
        setGroups([]);
        setGroupsError('Falha ao carregar grupos da Evolution — verifique a integracao em Administracao.');
      });
  };

  const loadDetail = (id: string) => {
    api.get<TicketDetail>(`/tickets/${id}`).then(setDetail).catch(() => setDetail(null));
  };

  useEffect(() => { loadConvos(); loadGroups(); const t = setInterval(loadConvos, 15000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!selected) return;
    loadDetail(selected);
    const t = setInterval(() => loadDetail(selected), 10000);
    return () => clearInterval(t);
  }, [selected]);
  // Rola a THREAD (não a página) para o fim ao carregar/atualizar as mensagens.
  // Usar scrollTop do próprio container evita que o navegador role a página inteira.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [detail]);

  const send = async () => {
    // Permite enviar só texto, só anexo(s), ou ambos.
    if ((!reply.trim() && pending.length === 0) || !selected) return;
    setSending(true);
    try {
      await api.post(`/tickets/${selected}/messages`, {
        body: reply,
        attachments: pending.length ? pending : undefined,
      });
      setReply('');
      setPending([]);
      loadDetail(selected);
      toast.success('Mensagem enviada');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao enviar');
    } finally {
      setSending(false);
    }
  };

  // Inicia a gravação de uma mensagem de voz usando o microfone do navegador.
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Seu navegador não suporta gravação de áudio.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefere OGG/Opus (melhor compatibilidade com áudio de voz do WhatsApp);
      // cai para webm quando o navegador não suportar ogg.
      const mime = MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/ogg') ? 'audio/ogg'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        // Encerra o microfone e envia o áudio como anexo pendente.
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || 'audio/webm';
        const ext = type.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size === 0) return;
        const file = new File([blob], `audio-${Date.now()}.${ext}`, { type });
        setUploading(true);
        try {
          const att = await uploadFile(file);
          setPending((cur) => [...cur, att]);
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Falha ao enviar o áudio');
        } finally {
          setUploading(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error('Não foi possível acessar o microfone. Verifique a permissão.');
    }
  };

  // Para a gravação (dispara o onstop, que faz o upload).
  const stopRecording = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  // Cancela a gravação sem enviar.
  const cancelRecording = () => {
    const rec = recorderRef.current;
    if (rec) {
      rec.onstop = null;
      rec.stop();
      rec.stream.getTracks().forEach((t) => t.stop());
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setRecording(false);
  };

  // Faz upload dos arquivos escolhidos e os adiciona à lista de anexos pendentes.
  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(Array.from(files).map(uploadFile));
      setPending((cur) => [...cur, ...uploaded]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao enviar anexo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const endConversation = async () => {
    if (!selected) return;
    const ok = await confirm({
      title: 'Encerrar atendimento',
      message: 'Encerrar este atendimento? O cliente receberá a mensagem de encerramento no WhatsApp.',
      confirmLabel: 'Encerrar',
    });
    if (!ok) return;
    setClosing(true);
    try {
      await api.patch(`/tickets/${selected}`, { status: TicketStatus.RESOLVED });
      toast.success('Atendimento encerrado');
      loadDetail(selected);
      loadConvos();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao encerrar');
    } finally {
      setClosing(false);
    }
  };

  // Inicia conversa (contato ou grupo). Para grupo, contact = JID (@g.us).
  const startConversation = async (contact: string, name: string, body: string) => {
    const t = await api.post<Ticket>('/whatsapp/start', { contact, name, body });
    setStarting(false);
    loadConvos();
    setTab('conversas');
    setSelected(t.id);
    toast.success('Conversa iniciada');
  };

  // Abre (ou reaproveita) a conversa de um grupo a partir da aba Grupos.
  const openGroup = async (g: WhatsAppGroup) => {
    // Se já existe conversa desse grupo carregada, seleciona; senão inicia uma.
    const existing = convos.find((c) => c.requesterContact === g.jid);
    if (existing) { setTab('conversas'); setSelected(existing.id); return; }
    setTab('conversas');
    try {
      const t = await api.post<Ticket>('/whatsapp/start', { contact: g.jid, name: g.name, body: '' });
      loadConvos();
      setSelected(t.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao abrir o grupo');
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return convos;
    return convos.filter((c) =>
      `${c.requesterName ?? ''} ${c.requesterContact ?? ''} ${c.subject} ${c.number}`.toLowerCase().includes(term),
    );
  }, [convos, search]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((g) => `${g.name} ${g.jid}`.toLowerCase().includes(term));
  }, [groups, search]);

  const visibleMessages = detail?.messages.filter((m) => m.direction !== MessageDirection.INTERNAL) ?? [];
  const activeIsGroup = isGroupTicket(detail?.ticket);
  // Vincular cliente só faz sentido para contato individual ainda não vinculado.
  const canLink = !!detail && !activeIsGroup && !detail.ticket.requesterId;

  return (
    <div className="full-bleed wa-page">
      <PageHeader eyebrow="Atendimento" title="WhatsApp" description="Converse com clientes e grupos pelo WhatsApp integrado." />
      <div className="wa-shell">
        {/* Lista (conversas ou grupos) */}
        <div className="wa-list">
          <div className="wa-list-head">
            <div className="seg" style={{ width: '100%' }}>
              <button className={tab === 'conversas' ? 'active' : ''} onClick={() => setTab('conversas')} style={{ flex: 1 }}>Conversas</button>
              <button className={tab === 'grupos' ? 'active' : ''} onClick={() => setTab('grupos')} style={{ flex: 1 }}>Grupos {groups.length ? `(${groups.length})` : ''}</button>
            </div>
            <div className="flex gap-2">
              <input
                className="input"
                placeholder={tab === 'grupos' ? '🔎 Buscar grupo' : '🔎 Buscar contato ou nº'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1 }}
              />
              {tab === 'conversas' && <button className="btn btn-sm btn-primary" onClick={() => setStarting(true)}>+ Nova</button>}
            </div>
          </div>

          <div className="wa-list-body">
            {tab === 'conversas' ? (
              filtered.length === 0 ? (
                <div className="empty" style={{ padding: 'var(--s5)' }}>
                  <div className="empty-icon">💬</div>
                  {convos.length === 0 ? 'Nenhuma conversa de WhatsApp.' : 'Nenhum resultado para a busca.'}
                </div>
              ) : filtered.map((c) => {
                const group = isGroupTicket(c);
                const name = group ? (c.requesterName ?? 'Grupo') : (c.requesterName ?? c.requesterContact ?? 'Contato');
                return (
                  <button key={c.id} onClick={() => setSelected(c.id)} className={`wa-convo ${selected === c.id ? 'active' : ''}`}>
                    <Avatar name={name} url={avatarUrlOf(c)} group={group} />
                    <div className="wa-convo-main">
                      <div className="wa-convo-top">
                        <span className="wa-convo-name">{name}</span>
                        <span className="wa-convo-time">{timeShort(c.lastMessageAt ?? c.updatedAt)}</span>
                      </div>
                      <div className="wa-convo-preview">{stripHtml(c.subject)}</div>
                    </div>
                  </button>
                );
              })
            ) : groupsError ? (
              // Aviso visível quando a Evolution falha (erro propagado pelo backend).
              <div
                className="empty"
                style={{
                  padding: 'var(--s5)',
                  background: 'var(--danger-soft)',
                  color: 'var(--danger)',
                  borderRadius: 'var(--r2, 8px)',
                  textAlign: 'center',
                }}
              >
                <div className="empty-icon">⚠️</div>
                {groupsError}
              </div>
            ) : (
              filteredGroups.length === 0 ? (
                <div className="empty" style={{ padding: 'var(--s5)' }}>
                  <div className="empty-icon">👥</div>
                  {groups.length === 0 ? 'Nenhum grupo encontrado na instância.' : 'Nenhum resultado para a busca.'}
                </div>
              ) : filteredGroups.map((g) => (
                <button key={g.jid} onClick={() => openGroup(g)} className="wa-convo">
                  <Avatar name={g.name} url={g.avatarUrl} group />
                  <div className="wa-convo-main">
                    <div className="wa-convo-top">
                      <span className="wa-convo-name">{g.name}</span>
                    </div>
                    <div className="wa-convo-preview">{g.size ? `${g.size} participantes` : 'Grupo'}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Conversa */}
        <div className="wa-chat">
          {!detail ? (
            <div className="empty" style={{ margin: 'auto' }}><div className="empty-icon">👈</div>Selecione uma conversa ou grupo.</div>
          ) : (
            <>
              <div className="wa-chat-head">
                <Avatar
                  name={detail.ticket.requesterName ?? detail.ticket.requesterContact ?? '?'}
                  url={avatarUrlOf(detail.ticket)}
                  group={activeIsGroup}
                />
                <div style={{ minWidth: 0 }}>
                  <div className="wa-chat-name">{detail.ticket.requesterName ?? detail.ticket.requesterContact}</div>
                  <div className="wa-chat-sub">
                    #{detail.ticket.number}
                    {activeIsGroup ? ' · Grupo' : (detail.ticket.requesterContact ? ` · ${detail.ticket.requesterContact}` : '')}
                    {detail.ticket.requesterId ? ' · cliente vinculado' : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2" style={{ marginLeft: 'auto' }}>
                  {/* Ligar para o contato (só conversa individual com número). */}
                  {!activeIsGroup && detail.ticket.requesterContact && !detail.ticket.requesterContact.includes('@') && (
                    <CallButton
                      number={detail.ticket.requesterContact}
                      ticketId={detail.ticket.id}
                      contactName={detail.ticket.requesterName ?? undefined}
                      label="Ligar"
                    />
                  )}
                  {canLink && (
                    <button className="btn btn-sm btn-ghost" onClick={() => setLinking(true)}>👤 Vincular cliente</button>
                  )}
                  {detail.ticket.status !== TicketStatus.RESOLVED && detail.ticket.status !== TicketStatus.CLOSED && (
                    <button className="btn btn-sm btn-ghost" style={{ color: 'var(--success)' }} onClick={endConversation} disabled={closing}>
                      {closing ? '...' : '✓ Encerrar'}
                    </button>
                  )}
                  <a href={`/tickets/${detail.ticket.id}`} className="btn btn-sm btn-ghost">Abrir chamado ↗</a>
                </div>
              </div>

              <div className="wa-thread" ref={threadRef}>
                {visibleMessages.length === 0 ? (
                  <div className="text-dim text-sm text-center" style={{ margin: 'auto' }}>Sem mensagens ainda.</div>
                ) : visibleMessages.map((m) => {
                  const out = m.direction === MessageDirection.OUTBOUND;
                  const hasBody = !!stripHtml(m.body);
                  return (
                    <div key={m.id} className={`wa-row ${out ? 'out' : 'in'}`}>
                      <div className={`wa-bubble ${out ? 'out' : 'in'}`}>
                        {out && m.authorName ? (
                          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>{m.authorName}</div>
                        ) : null}
                        {hasBody && <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: m.body }} />}
                        {m.attachments?.length ? (
                          <div className="flex flex-col gap-2" style={{ marginTop: hasBody ? 6 : 0 }}>
                            {m.attachments.map((a) => <AttachmentView key={a.id} att={a} outbound={out} />)}
                          </div>
                        ) : null}
                        <div className="wa-bubble-time">{new Date(m.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              {/* Anexos pendentes (mídia a enviar). */}
              {pending.length > 0 && (
                <div className="flex flex-wrap gap-2" style={{ padding: '8px var(--s3) 0' }}>
                  {pending.map((a) => (
                    <span key={a.id} className="badge badge-normal">
                      📎 {a.fileName}
                      <span style={{ cursor: 'pointer' }} onClick={() => setPending((cur) => cur.filter((x) => x.id !== a.id))}> ✕</span>
                    </span>
                  ))}
                </div>
              )}

              <div className="wa-composer">
                <input ref={fileRef} type="file" multiple hidden onChange={(e) => onPickFiles(e.target.files)} />
                <button
                  className="btn btn-ghost btn-icon"
                  type="button"
                  title="Anexar imagem, vídeo, áudio ou arquivo"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading || recording}
                >
                  {uploading ? '…' : '📎'}
                </button>
                {/* Gravação de áudio (mensagem de voz). */}
                {recording ? (
                  <>
                    <button className="btn btn-danger btn-icon" type="button" title="Parar e anexar o áudio" onClick={stopRecording}>⏹</button>
                    <button className="btn btn-ghost btn-icon" type="button" title="Cancelar gravação" onClick={cancelRecording}>✕</button>
                    <span className="text-sm" style={{ alignSelf: 'center', color: 'var(--danger)' }}>● gravando…</span>
                  </>
                ) : (
                  <button className="btn btn-ghost btn-icon" type="button" title="Gravar áudio" onClick={startRecording} disabled={uploading}>🎤</button>
                )}
                <textarea
                  className="input wa-composer-input"
                  value={reply}
                  rows={1}
                  onChange={(e) => setReply(e.target.value)}
                  // Enter envia; Shift+Enter quebra linha (não envia). O textarea
                  // permite redimensionar arrastando o canto (resize: vertical).
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  // Colar imagem direto (Ctrl+V) anexa como mídia pendente.
                  onPaste={(e) => {
                    const imgs = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
                    if (imgs.length) {
                      e.preventDefault();
                      const dt = new DataTransfer();
                      imgs.forEach((f) => dt.items.add(f));
                      onPickFiles(dt.files);
                    }
                  }}
                  placeholder={activeIsGroup ? 'Mensagem para o grupo... (Enter envia, Shift+Enter nova linha)' : 'Digite uma mensagem... (Enter envia, Shift+Enter nova linha)'}
                />
                <button className="btn btn-primary" onClick={send} disabled={sending || uploading || recording || (!reply.trim() && pending.length === 0)}>
                  {sending ? '...' : 'Enviar ➤'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {starting && <StartConversationModal onClose={() => setStarting(false)} onStart={startConversation} />}
      {linking && detail && (
        <LinkClientModal
          ticket={detail.ticket}
          onClose={() => setLinking(false)}
          onDone={() => { setLinking(false); loadDetail(detail.ticket.id); loadConvos(); }}
        />
      )}
    </div>
  );
}

// Contato cadastrado com telefone (para busca ao iniciar conversa).
interface PhoneContact { id: string; name: string; phone: string; email?: string }

// Modal para iniciar uma conversa proativa de WhatsApp (número + mensagem).
// Permite buscar um contato já cadastrado (por nome/telefone) e preencher os campos.
function StartConversationModal({
  onClose, onStart,
}: {
  onClose: () => void;
  onStart: (contact: string, name: string, body: string) => Promise<void>;
}) {
  const toast = useToast();
  const [contact, setContact] = useState('');
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  // Busca de contatos cadastrados.
  const [contacts, setContacts] = useState<PhoneContact[]>([]);
  const [contactSearch, setContactSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Carrega os usuários que possuem telefone (clientes ou qualquer cadastro com número).
  useEffect(() => {
    api.get<Paginated<User>>('/users')
      .then((r) => {
        const list: PhoneContact[] = [];
        for (const u of r.items) {
          const numbers = [u.phone, ...(u.phones ?? [])].filter((p): p is string => !!p && !!p.replace(/\D/g, ''));
          for (const p of numbers) list.push({ id: `${u.id}:${p}`, name: u.name, phone: p.replace(/\D/g, ''), email: u.email });
        }
        setContacts(list);
      })
      .catch(() => setContacts([]));
  }, []);

  const suggestions = useMemo(() => {
    const term = contactSearch.trim().toLowerCase();
    if (!term) return [];
    return contacts
      .filter((c) => `${c.name} ${c.phone} ${c.email ?? ''}`.toLowerCase().includes(term))
      .slice(0, 8);
  }, [contacts, contactSearch]);

  const pickContact = (c: PhoneContact) => {
    setContact(c.phone);
    setName(c.name);
    setContactSearch(`${c.name} · ${c.phone}`);
    setShowSuggestions(false);
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    const digits = contact.replace(/\D/g, '');
    if (digits.length < 10) return toast.error('Informe o número com DDD (e DDI). Ex.: 5511999999999');
    if (!body.trim()) return toast.error('Escreva a mensagem inicial.');
    setBusy(true);
    try {
      await onStart(digits, name, body);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao iniciar conversa');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title="Nova conversa de WhatsApp"
      onClose={onClose}
      maxWidth={420}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Enviando...' : 'Iniciar conversa'}</button>
        </>
      }
    >
      <form onSubmit={submit}>
        {/* Busca de contato já cadastrado (autocomplete por nome/telefone). */}
        <div className="field" style={{ position: 'relative' }}>
          <label className="label">Buscar contato cadastrado</label>
          <input
            className="input"
            value={contactSearch}
            onChange={(e) => { setContactSearch(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            placeholder="🔎 Nome ou telefone..."
            autoComplete="off"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              style={{
                position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, marginTop: 4,
                background: 'var(--surface)', border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow)', maxHeight: 220, overflowY: 'auto',
              }}
            >
              {suggestions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pickContact(c)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%',
                    padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--border)',
                    background: 'transparent', cursor: 'pointer', textAlign: 'left', color: 'var(--text)',
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</span>
                  <span className="text-dim text-xs">{c.phone}{c.email ? ` · ${c.email}` : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="field">
          <label className="label">Número (com DDI e DDD)</label>
          <input className="input" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="5511999999999" inputMode="numeric" />
        </div>
        <div className="field">
          <label className="label">Nome do contato (opcional)</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: João Silva" />
        </div>
        <div className="field mb-0">
          <label className="label">Mensagem inicial</label>
          <textarea className="input" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Olá! Aqui é o suporte da Makweb..." />
        </div>
        <button type="submit" style={{ display: 'none' }} />
      </form>
    </Modal>
  );
}

// Modal para vincular o contato de WhatsApp a um cliente existente ou criar um novo.
function LinkClientModal({
  ticket, onClose, onDone,
}: {
  ticket: Ticket;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const phone = (ticket.requesterContact ?? '').replace(/\D/g, '');
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [clients, setClients] = useState<{ id: string; name: string; email: string }[]>([]);
  const [clientId, setClientId] = useState('');
  const [form, setForm] = useState({ name: ticket.requesterName ?? '', email: '', companyId: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<Paginated<Company>>('/companies').then((r) => setCompanies(r.items)).catch(() => {});
    api.get<Paginated<{ id: string; name: string; email: string; role: string }>>('/users')
      .then((r) => setClients(r.items.filter((u) => u.role === 'client').map(({ id, name, email }) => ({ id, name, email }))))
      .catch(() => {});
  }, []);

  // Vincula: grava o telefone no cadastro do cliente. A partir daí, novas mensagens
  // deste número são associadas automaticamente ao cliente (via casamento por telefone).
  const linkExisting = async () => {
    if (!clientId) { toast.error('Selecione um cliente.'); return; }
    setBusy(true);
    try {
      await api.patch(`/users/${clientId}`, { phone });
      toast.success('Telefone vinculado ao cliente.');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao vincular');
    } finally {
      setBusy(false);
    }
  };

  const createNew = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.companyId) {
      toast.error('Preencha nome, e-mail e empresa.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/users', {
        name: form.name.trim(),
        email: form.email.trim(),
        role: UserRole.CLIENT,
        companyId: form.companyId,
        phone,
      });
      toast.success('Cliente criado e telefone vinculado.');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao criar cliente');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title="Vincular contato a um cliente"
      onClose={onClose}
      maxWidth={480}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          {mode === 'existing'
            ? <button type="button" className="btn btn-primary" onClick={linkExisting} disabled={busy}>{busy ? '...' : 'Vincular'}</button>
            : <button type="submit" form="wa-new-client" className="btn btn-primary" disabled={busy}>{busy ? '...' : 'Criar e vincular'}</button>}
        </>
      }
    >
      <p className="text-muted text-sm mb-4">Telefone do contato: <strong>{phone || '—'}</strong></p>
      <div className="seg mb-4">
        <button className={mode === 'existing' ? 'active' : ''} onClick={() => setMode('existing')}>Cliente existente</button>
        <button className={mode === 'new' ? 'active' : ''} onClick={() => setMode('new')}>Novo cliente</button>
      </div>

      {mode === 'existing' ? (
        <div className="field mb-0">
          <label className="label">Cliente</label>
          <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Selecione o cliente</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.email}</option>)}
          </select>
          <p className="text-dim text-xs mt-1">O telefone deste contato será salvo no cadastro do cliente.</p>
        </div>
      ) : (
        <form id="wa-new-client" onSubmit={createNew}>
          <div className="field"><label className="label">Nome</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div className="field"><label className="label">E-mail</label><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          <div className="field mb-0"><label className="label">Empresa</label>
            <select className="select" value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })} required>
              <option value="">Selecione a empresa</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </form>
      )}
    </Modal>
  );
}
